/*
 *  saga to load auth user details from the just token
 */
import { cloneDeep } from 'lodash'
import { fork, put, delay, select, takeEvery } from 'redux-saga/effects'

import { createKeywordsSdTask, getKeywordsSdReport } from '../../utils/api'
import { generateKey } from '../../utils/functions'
import {
  UPDATE_KEYWORDS_METRICS,
  KeywordsSDBank,
  KEY_KEYWORDS_SD_VALUE,
  KEYWORDS_SD_POLL_INTERVAL,
  loadKeywordsFromCacheSuccess,
  ADD_REPORT_ID_TO_POLLING,
  REMOVE_REPORT_IDS_FROM_POLLING,
  SET_POLLING,
  mergeKeywordsSd,
  isValidSD,
  getPendingUpdatedAt,
  updateMetricsLimits,
} from '../actions/sdManager'
import {
  selectIsPolling,
  selectKeywordMetricsLimits,
  selectKeywordsSD,
} from '../selectors/sdManager'
import { updateUserLimits } from '../actions/global'
import { KEYWORD_METRICS } from '../../utils/consts.mjs'

export default function* sdManagerSaga() {
  yield fork(loadCachedKeywords)
  yield takeEvery(UPDATE_KEYWORDS_METRICS.action, updateKeywordsMetrics)
}

function* loadCachedKeywords() {
  try {
    const keywordsSdStr = KeywordsSDBank.getItem(KEY_KEYWORDS_SD_VALUE)
    if (keywordsSdStr) {
      yield put(
        loadKeywordsFromCacheSuccess(
          mergeKeywordsSd(JSON.parse(keywordsSdStr), []), // there is logic to remove expired sd keywords in mergeKeywordsSd
        ),
      )
    } else {
      yield put(loadKeywordsFromCacheSuccess({}))
    }
  } catch (e) {
    yield loadKeywordsFromCacheSuccess({})
  }
}

function* updateKeywordsMetrics(action) {
  const { keywords, isSD } = action.payload
  const keywordsBank = yield select(selectKeywordsSD())
  const newKeywords = isSD
    ? keywords.filter(
        (kw) =>
          !isValidSD(
            keywordsBank[generateKey([kw.lang, kw.loc_id, kw.keyword])],
          ),
      )
    : keywords
  const metricsLImits = yield select(selectKeywordMetricsLimits())
  if (metricsLImits.used + newKeywords.length > metricsLImits.limits) {
    yield put({
      type: UPDATE_KEYWORDS_METRICS.failed,
      payload: newKeywords,
      error: new Error('Keyword Metrics Limits Exceeded'),
    })
    return
  }
  try {
    if (newKeywords.length > 0) {
      // we should put placeholder keywords in redux store first because createKeywordsSdTask api call response take some time and when user go and back pagination while waiting for this call, it will call api with duplicated keywords.
      yield put({
        type: UPDATE_KEYWORDS_METRICS.polling,
        payload: {
          newKeywords: newKeywords.reduce((prev, kw) => {
            prev[generateKey([kw.lang, kw.loc_id, kw.keyword])] = {
              ...kw,
              status: 'pending',
              updated_at: getPendingUpdatedAt(), // set keyword updated_at to be expired after 10 seconds
            }
            return cloneDeep(prev)
          }, {}),
          metric: isSD ? KEYWORD_METRICS.SD : KEYWORD_METRICS.SI,
        },
      })

      const res = yield createKeywordsSdTask(newKeywords, isSD)
      const { reportId } = res.data
      if (reportId) {
        yield put({
          type: ADD_REPORT_ID_TO_POLLING.action,
          reportId,
        })
        const isPolling = yield select(selectIsPolling())

        if (!isPolling) {
          yield fork(pollSdTaskHandler)
        }
      } else {
        throw new Error('No Report Id returned')
      }
    }
  } catch (error) {
    console.error('error', error)
    yield put({
      type: UPDATE_KEYWORDS_METRICS.failed,
      payload: newKeywords,
      error,
    })
  }
}

function* pollSdTaskHandler() {
  let retry = 3
  while (true) {
    try {
      const reportIds = yield select((state) => state.sdManager.reportIds)
      if (reportIds.length === 0) break
      yield put({
        type: SET_POLLING.action,
        isPolling: true,
      })
      const res = yield getKeywordsSdReport(reportIds)
      if (res.data && res.data.reports) {
        let hasPendingReports = false
        const reportIdsFromAPI = Object.keys(res.data.reports)
        const finishedReportIds = []

        yield put(updateMetricsLimits(res.data.limits))
        yield put(updateUserLimits(res.data.limits))

        for (let i = 0; i < reportIdsFromAPI.length; i += 1) {
          const reportId = reportIdsFromAPI[i]
          let hasPending = false
          const newKeywords = {}
          const report = res.data.reports[reportId]
          const { metric } = report

          report.keywords.forEach((kw) => {
            hasPending = hasPending || kw.status === 'pending'
            const key = generateKey([kw.lang, kw.loc_id, kw.keyword])
            if (kw.status === 'pending') {
              newKeywords[key] = {
                ...kw,
                updated_at: getPendingUpdatedAt(),
              }
            } else {
              newKeywords[key] = kw
            }
          })

          yield put({
            type: !hasPending
              ? UPDATE_KEYWORDS_METRICS.success
              : UPDATE_KEYWORDS_METRICS.polling,
            payload: { newKeywords, metric },
          })

          if (!hasPending) {
            finishedReportIds.push(reportId)
          }

          hasPendingReports = hasPendingReports || hasPending
        }

        yield put({
          type: REMOVE_REPORT_IDS_FROM_POLLING.action,
          reportIds: finishedReportIds,
        })

        if (!hasPendingReports) {
          yield put({
            type: SET_POLLING.action,
            isPolling: false,
          })
          break
        }
      } else {
        throw new Error('No Report')
      }
    } catch (error) {
      console.error('error', error)
      yield put({
        type: UPDATE_KEYWORDS_METRICS.failed,
        error,
      })
      retry -= 1
      if (retry < 1) {
        yield put({
          type: SET_POLLING.action,
          isPolling: false,
        })
        break
      }
    } finally {
      yield delay(KEYWORDS_SD_POLL_INTERVAL)
    }
  }
}
